import { z } from 'zod';
import type { TFunction } from 'i18next';

/**
 * Les schémas sont des fabriques et non des constantes de module : un schéma
 * construit à l'import figerait ses messages dans la langue active au
 * chargement, sans suivre `i18n.changeLanguage`.
 *
 * À consommer via `useLocalizedSchema` (src/hooks/useLocalizedSchema.ts).
 */

export const createLoginSchema = (t: TFunction) =>
  z.object({
    username: z
      .string()
      .min(1, t('validations.emailRequired'))
      .email(t('validations.emailInvalid')),
    password: z.string().min(8, t('validations.passwordMin')),
  });

export const createEmailSchema = (t: TFunction) =>
  z.object({
    email: z
      .string()
      .min(1, t('validations.emailRequired'))
      .email(t('validations.emailInvalid')),
  });

/**
 * Règles d'un nouveau mot de passe, alignées sur la politique du realm Keycloak
 * (length(12) and maxLength(25) and upperCase(1) and digits(3) and specialChars(1)).
 * Partagées par le schéma ci-dessous et l'aide affichée (PasswordRules).
 */
export const PASSWORD_POLICY = { minLength: 12, maxLength: 25, minDigits: 3 };

export const passwordChecks = (password: string) => ({
  length: password.length >= PASSWORD_POLICY.minLength && password.length <= PASSWORD_POLICY.maxLength,
  uppercase: /[A-Z]/.test(password),
  digits: (password.match(/[0-9]/g) || []).length >= PASSWORD_POLICY.minDigits,
  // Comme Keycloak : tout caractère non alphanumérique compte comme spécial
  special: /[^A-Za-z0-9]/.test(password),
});

export const createPasswordSchema = (t: TFunction) =>
  z
    .object({
      password: z
        .string()
        .min(PASSWORD_POLICY.minLength, t('general.passwordTooShort'))
        .max(PASSWORD_POLICY.maxLength, t('general.passwordTooLong'))
        .refine((p) => passwordChecks(p).uppercase, t('general.passwordMissingUppercase'))
        .refine((p) => passwordChecks(p).digits, t('general.passwordMissingDigit'))
        .refine((p) => passwordChecks(p).special, t('general.passwordMissingSpecial')),
      confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('general.passwordsDontMatch'),
      path: ['confirmPassword'],
    });

export type LoginFormData = z.infer<ReturnType<typeof createLoginSchema>>;
export type EmailFormData = z.infer<ReturnType<typeof createEmailSchema>>;
export type PasswordFormData = z.infer<ReturnType<typeof createPasswordSchema>>;
